import React, { useEffect, useState } from "react";
import DashboardLayout from "../../../layoutcontainers/DashboardLayout";
import MaterialTypography from "../../../components/MaterialTypography";
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Grid,
  Stack,
  Chip,
  Divider,
} from "@mui/material";
import MaterialButton from "../../../components/MaterialButton";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import BASEURL from "../../Utils/Baseurl";
import { useLoading } from "../../../context/loading";

const formatTime12hr = (timeStr) => {
  if (!timeStr) return "-";
  const parts = timeStr.split(":");
  const hour = parseInt(parts[0], 10);
  const min = parts[1];
  const ampm = hour >= 12 ? "PM" : "AM";
  const hr12 = hour % 12 || 12;
  return `${hr12}:${min} ${ampm}`;
};

function Shift_viewscreen() {
  const [shiftData, setShiftData] = useState(null);
  const navigate = useNavigate();
  const { setCirleLoading } = useLoading();

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const id = queryParams.get("id");
    if (id) {
      getInitData(id);
    }
  }, []);

  const getInitData = async (id) => {
    setCirleLoading(true);
    const query = `query getShiftById($id: ID!) {
  shiftById(id: $id) {
    id
    name
    startTime
    endTime
    totalWorkingHours
    shiftType
    isOvernight
    gracePeriodMinutes
  }
}`;
    try {
      const response = await axios.post(
        BASEURL,
        { query: query, variables: { id } },
        { headers: { "Content-Type": "application/json" } }
      );

      const data = response.data.data.shiftById;
      setShiftData(data);
    } catch (error) {
      console.error("ResponseError:", error);
    } finally {
      setCirleLoading(false);
    }
  };

  const handleClose = () => {
    navigate("/shift");
  };

  const handleEdit = () => {
    navigate(`/shift/shift-details?id=${shiftData?.id}`);
  };

  if (!shiftData) {
    return (
      <DashboardLayout>
        <Card>
          <CardContent>
            <MaterialTypography variant="body2" textAlign="center">
              Loading shift details...
            </MaterialTypography>
          </CardContent>
        </Card>
      </DashboardLayout>
    );
  }

  const durationHrs = shiftData.totalWorkingHours
    ? (shiftData.totalWorkingHours / 3600).toFixed(1)
    : "-";

  const hasTiming = shiftData.startTime && shiftData.endTime;

  return (
    <DashboardLayout>
      <Card>
        <CardHeader
          title={
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="h5" fontWeight="bold">
                  Shift Details
                </Typography>
                {shiftData.shiftType && (
                  <Chip
                    label={shiftData.shiftType}
                    color={
                      shiftData.shiftType === "NIGHT"
                        ? "secondary"
                        : shiftData.shiftType === "DAY"
                        ? "primary"
                        : "default"
                    }
                    size="small"
                    variant="outlined"
                  />
                )}
                {shiftData.isOvernight && (
                  <Chip label="Overnight" color="warning" size="small" />
                )}
              </Stack>
              <MaterialButton
                variant="gradient"
                color="dark"
                size="small"
                onClick={handleEdit}
              >
                Edit
              </MaterialButton>
            </Stack>
          }
        />
        <Divider />
        <CardContent>
          <Grid container spacing={3} marginLeft={"2%"}>
            {/* Row 1 */}
            <Grid item xs={4}>
              <Stack spacing={1}>
                <MaterialTypography variant="caption" color="text" fontWeight="bold">
                  Shift Name
                </MaterialTypography>
                <MaterialTypography variant="h6" fontWeight="medium">
                  {shiftData.name}
                </MaterialTypography>
              </Stack>
            </Grid>

            <Grid item xs={4}>
              <Stack spacing={1}>
                <MaterialTypography variant="caption" color="text" fontWeight="bold">
                  Shift Type
                </MaterialTypography>
                <MaterialTypography variant="h6" fontWeight="medium">
                  {shiftData.shiftType || "General"}
                </MaterialTypography>
              </Stack>
            </Grid>

            <Grid item xs={4}>
              <Stack spacing={1}>
                <MaterialTypography variant="caption" color="text" fontWeight="bold">
                  Duration
                </MaterialTypography>
                <MaterialTypography variant="h6" fontWeight="medium">
                  {durationHrs} hours
                </MaterialTypography>
              </Stack>
            </Grid>

            {/* Row 2 */}
            <Grid item xs={4}>
              <Stack spacing={1}>
                <MaterialTypography variant="caption" color="text" fontWeight="bold">
                  Start Time
                </MaterialTypography>
                <MaterialTypography variant="h6" fontWeight="medium">
                  {hasTiming ? formatTime12hr(shiftData.startTime) : "-"}
                </MaterialTypography>
              </Stack>
            </Grid>

            <Grid item xs={4}>
              <Stack spacing={1}>
                <MaterialTypography variant="caption" color="text" fontWeight="bold">
                  End Time
                </MaterialTypography>
                <MaterialTypography variant="h6" fontWeight="medium">
                  {hasTiming ? formatTime12hr(shiftData.endTime) : "-"}
                </MaterialTypography>
              </Stack>
            </Grid>

            <Grid item xs={4}>
              <Stack spacing={1}>
                <MaterialTypography variant="caption" color="text" fontWeight="bold">
                  Grace Period
                </MaterialTypography>
                <MaterialTypography variant="h6" fontWeight="medium">
                  {shiftData.gracePeriodMinutes ?? 15} minutes
                </MaterialTypography>
              </Stack>
            </Grid>

            {/* Row 3 */}
            <Grid item xs={4}>
              <Stack spacing={1}>
                <MaterialTypography variant="caption" color="text" fontWeight="bold">
                  Overnight Shift
                </MaterialTypography>
                <MaterialTypography variant="h6" fontWeight="medium">
                  {shiftData.isOvernight ? (
                    <Chip label="Yes" color="warning" size="small" />
                  ) : (
                    <Chip label="No" size="small" variant="outlined" />
                  )}
                </MaterialTypography>
              </Stack>
            </Grid>

            {hasTiming && (
              <Grid item xs={4}>
                <Stack spacing={1}>
                  <MaterialTypography variant="caption" color="text" fontWeight="bold">
                    Timing
                  </MaterialTypography>
                  <MaterialTypography variant="h6" fontWeight="medium">
                    {formatTime12hr(shiftData.startTime)} -{" "}
                    {formatTime12hr(shiftData.endTime)}
                  </MaterialTypography>
                </Stack>
              </Grid>
            )}

            {/* Close Button */}
            <Grid item xs={12} textAlign="center" marginTop={4}>
              <MaterialButton
                variant="gradient"
                color="dark"
                onClick={handleClose}
                style={{ maxWidth: "100px", minWidth: "100px" }}
              >
                Close
              </MaterialButton>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </DashboardLayout>
  );
}

export default Shift_viewscreen;
